/**
 * PATH: apps/email/src/pages/DraftingPage.tsx
 * FILE_ID: EMAIL-DRAFTING-PAGE-001
 * PURPOSE: Smart host for the shared <DraftingStudio> in the Email Hub.
 *   Owns persistence (save/open/list via /api/drafting/) and the email
 *   hand-off (Insert inline / Attach) so the @quotech/drafting package stays
 *   transport-agnostic (callbacks in, no app coupling).
 * WHY: P2 of the Drafting Studio — backend-backed drawings + "put this drawing
 *   in an email" using the SAME compose-pending hand-off the markup drawer and
 *   PDF editor already use. See docs/scopes/DRAFTING_STUDIO_SCOPE.md §6 + §9.
 * FLOW:
 *   studio onChange → irRef (latest IR, no re-render)
 *   Save  → useDraftingPersistence.save(irRef)
 *   Open  → persistence.open(id) → remount studio with current_ir
 *   Insert/Attach → composePendingStore.setPending(...) → navigate(compose)
 */
import { useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { DraftingStudio, useDraftingPersistence } from '@quotech/drafting'
import type { DrawingIR } from '@quotech/drafting'
import {
  useComposePendingStore,
  composeUrlFor,
  type ComposeIntent,
} from '@quotech/email-hub'

const NEW_INTENT: ComposeIntent = { kind: 'new' }
const toolBtn =
  'inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800'

export default function DraftingPage() {
  const navigate = useNavigate()
  const setPending = useComposePendingStore((s) => s.setPending)
  const {
    list, loadedIr, studioKey, busy, error, refresh, save, open, startNew,
    uploadSketch, generate, refine,
  } = useDraftingPersistence()

  // Latest IR kept in a ref — the studio fires onChange on every edit; we only
  // need the value at Save / Insert / Attach time, so a ref avoids a re-render
  // (and a stale-closure) on every keystroke.
  const irRef = useRef<DrawingIR | null>(null)

  useEffect(() => {
    void refresh()
  }, [refresh])

  const onChange = useCallback((ir: DrawingIR) => {
    irRef.current = ir
  }, [])

  const onSave = useCallback(() => {
    if (irRef.current) void save(irRef.current)
  }, [save])

  const subject = () => irRef.current?.meta.title || 'Drawing'

  const onInsertImage = useCallback(
    (dataUrl: string) => {
      setPending({ files: [], intent: NEW_INTENT, inlineImageDataUrl: dataUrl, subject: subject() })
      navigate(composeUrlFor(NEW_INTENT))
    },
    [navigate, setPending],
  )

  const onAttach = useCallback(
    (file: File) => {
      setPending({
        files: [file],
        intent: NEW_INTENT,
        subject: subject(),
        bodyPrefix: 'Please find the attached drawing.',
      })
      navigate(composeUrlFor(NEW_INTENT))
    },
    [navigate, setPending],
  )

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
        <button type="button" className={toolBtn} onClick={startNew} disabled={busy}>
          New
        </button>
        <button type="button" className={toolBtn} onClick={onSave} disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <label className="sr-only" htmlFor="drafting-open">
          Open a saved drawing
        </label>
        <select
          id="drafting-open"
          className={`${toolBtn} max-w-[16rem]`}
          value=""
          disabled={busy || list.length === 0}
          onChange={(e) => {
            if (e.target.value) void open(e.target.value)
          }}
        >
          <option value="">
            {list.length === 0 ? 'No saved drawings' : `Open… (${list.length})`}
          </option>
          {list.map((d) => (
            <option key={d.id} value={d.id}>
              {d.title} · v{d.version_count}
            </option>
          ))}
        </select>
        {error && (
          <span className="text-xs text-rose-600 dark:text-rose-400" role="alert">
            {error}
          </span>
        )}
      </div>
      <div className="min-h-0 flex-1">
        <DraftingStudio
          key={studioKey}
          initialDrawing={loadedIr}
          onChange={onChange}
          onInsertImage={onInsertImage}
          onAttach={onAttach}
          onGenerate={generate}
          onRefine={refine}
          onUploadSketch={uploadSketch}
        />
      </div>
    </div>
  )
}
